import { useMemo, useRef, useState } from 'react'
import {
  AppDialog,
  Checkbox,
  DatePicker,
  FilePicker,
  FileRow,
  Textarea,
  TextInput,
  TimePicker,
  Toggle,
  type SelectOption,
} from '../Common'
import { useNls } from '../../nls/nlsProvider'
import { EduAudiencePicker } from './EduAudiencePicker'
import type { TimelineElement } from '../../context/TimelineContext'
import {
  createEduEvent,
  updateEduEvent,
  uploadEduEventFiles,
  generateEduRequestId,
  mapEduEventError,
  type EduEventItem,
  type EduEventAttachment,
  type EduEventAudienceKind,
} from '../../services/eduEventService'
import {
  DEFAULT_EDU_TIMEZONE,
  zonedWallClockToUtcIso,
  utcIsoToZonedParts,
} from '../../utils/eduZonedTime'
import './eduEvents.css'

export interface EduEventEditorDialogProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  activeRole: string
  isOwner: boolean
  currentUserId: string
  elements: TimelineElement[]
  event?: EduEventItem
  initialClassId?: string
  timezone?: string
  onSaved: () => void
}

export const EduEventEditorDialog = ({
  isOpen,
  onClose,
  workspaceId,
  activeRole,
  isOwner,
  currentUserId,
  elements,
  event,
  initialClassId,
  timezone,
  onSaved,
}: EduEventEditorDialogProps) => {
  const { t } = useNls()
  const isEditing = Boolean(event)
  const isStaffAdmin = isOwner || activeRole === 'ADMIN'

  const requestIdRef = useRef<string>(generateEduRequestId('event'))

  // Filter available roots (YEAR or PROGRAMME)
  const availableRoots = useMemo(() => {
    return elements.filter(
      (el) => el.edu?.kind === 'YEAR' || el.edu?.kind === 'PROGRAMME',
    )
  }, [elements])

  // Filter available class/group nodes where current staff can post
  const availableClassNodes = useMemo(() => {
    return elements.filter((el) => {
      const isClassOrGroup =
        el.edu?.kind === 'CLASS' ||
        el.edu?.kind === 'GROUP' ||
        el.edu?.kind === 'CLIENT'
      if (!isClassOrGroup) return false
      if (isStaffAdmin) return true
      return Boolean(el.edu?.teachers?.[currentUserId])
    })
  }, [elements, isStaffAdmin, currentUserId])

  // Audience options
  const audienceOptions = useMemo<SelectOption<EduEventAudienceKind>[]>(() => {
    const opts: SelectOption<EduEventAudienceKind>[] = []
    if (isStaffAdmin) {
      opts.push({
        value: 'ALL',
        label: t('NLSEduEventAudienceAll'),
      })
    }
    opts.push({
      value: 'ROOT',
      label: t('NLSEduEventAudienceRoot'),
    })
    opts.push({
      value: 'NODES',
      label: t('NLSEduEventAudienceNodes'),
    })
    return opts
  }, [isStaffAdmin, t])

  // Form State
  const [audienceKind, setAudienceKind] = useState<EduEventAudienceKind>(() => {
    if (event?.audience.kind) return event.audience.kind
    if (isStaffAdmin) return 'ALL'
    return 'NODES'
  })

  const [selectedRootId, setSelectedRootId] = useState<string>(() => {
    if (event?.audience.rootId) return event.audience.rootId
    return availableRoots[0]?.id || ''
  })

  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>(() => {
    if (event?.audience.nodeIds?.length) return event.audience.nodeIds
    if (initialClassId) return [initialClassId]
    if (availableClassNodes[0]) return [availableClassNodes[0].id]
    return []
  })

  const [title, setTitle] = useState<string>(event?.title || '')
  const [description, setDescription] = useState<string>(event?.description || '')
  const [location, setLocation] = useState<string>(event?.location || '')
  const [onlineUrl, setOnlineUrl] = useState<string>(event?.onlineUrl || '')

  const [allDay, setAllDay] = useState<boolean>(() => (event ? Boolean(event.allDay) : false))

  const effectiveTimezone = event?.timezone || timezone || DEFAULT_EDU_TIMEZONE

  const [startDate, setStartDate] = useState<string>(() => {
    return event?.startAt
      ? utcIsoToZonedParts(event.startAt, effectiveTimezone).date
      : utcIsoToZonedParts(new Date().toISOString(), effectiveTimezone).date
  })
  const [endDate, setEndDate] = useState<string>(() => {
    return event?.endAt
      ? utcIsoToZonedParts(event.endAt, effectiveTimezone).date
      : utcIsoToZonedParts(new Date(Date.now() + 60 * 60 * 1000).toISOString(), effectiveTimezone).date
  })
  const [startTime, setStartTime] = useState<string>(() => {
    return event?.startAt
      ? utcIsoToZonedParts(event.startAt, effectiveTimezone).time
      : utcIsoToZonedParts(new Date().toISOString(), effectiveTimezone).time
  })
  const [endTime, setEndTime] = useState<string>(() => {
    return event?.endAt
      ? utcIsoToZonedParts(event.endAt, effectiveTimezone).time
      : utcIsoToZonedParts(new Date(Date.now() + 60 * 60 * 1000).toISOString(), effectiveTimezone).time
  })

  const [rsvpEnabled, setRsvpEnabled] = useState<boolean>(Boolean(event?.rsvpEnabled))
  const [rsvpDeadlineDate, setRsvpDeadlineDate] = useState<string>(() => {
    if (event?.rsvpDeadline) {
      return utcIsoToZonedParts(event.rsvpDeadline, effectiveTimezone).date
    }
    return ''
  })
  const [rsvpDeadlineTime, setRsvpDeadlineTime] = useState<string>(() => {
    if (event?.rsvpDeadline) {
      return utcIsoToZonedParts(event.rsvpDeadline, effectiveTimezone).time
    }
    return '23:59'
  })

  // Existing attachments (for edit mode)
  const [existingAttachments, setExistingAttachments] = useState<EduEventAttachment[]>(
    () => event?.attachments || [],
  )

  // Reminders: multi-select of [10080, 1440, 60, 15]
  const [reminders, setReminders] = useState<number[]>(() => event?.reminderOffsetsMin || [1440, 60])

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [uploadStatus, setUploadStatus] = useState<string | null>(null)

  const toggleReminder = (minutes: number) => {
    setReminders((prev) => {
      if (prev.includes(minutes)) {
        return prev.filter((m) => m !== minutes)
      }
      if (prev.length >= 3) {
        return prev
      }
      return [...prev, minutes]
    })
  }

  const handleSubmit = async () => {
    setErrorMsg(null)

    // 1. Validation
    const cleanTitle = title.trim()
    if (!cleanTitle) {
      setErrorMsg(t('NLSEduEventValidationErrorTitleRequired'))
      return
    }

    if (audienceKind === 'ROOT' && !selectedRootId) {
      setErrorMsg(t('NLSEduEventValidationErrorAudienceRequired'))
      return
    }

    if (audienceKind === 'NODES' && selectedNodeIds.length === 0) {
      setErrorMsg(t('NLSEduEventValidationErrorAudienceRequired'))
      return
    }

    const cleanUrl = onlineUrl.trim()
    if (cleanUrl) {
      if (!cleanUrl.startsWith('https://')) {
        setErrorMsg(t('NLSEduEventValidationErrorHttpsUrl'))
        return
      }
    }

    // 2. Date Bounds
    let startIso: string
    let endIso: string

    if (allDay) {
      startIso = startDate
      endIso = endDate || startDate
      if (endIso < startIso) {
        setErrorMsg(t('NLSEduEventValidationErrorEndBeforeStart'))
        return
      }
      const sMs = new Date(`${startIso}T00:00:00Z`).getTime()
      const eMs = new Date(`${endIso}T23:59:59Z`).getTime()
      if (eMs - sMs > 31 * 24 * 60 * 60 * 1000) {
        setErrorMsg(t('NLSEduEventValidationErrorTooLong'))
        return
      }
    } else {
      startIso = zonedWallClockToUtcIso(startDate, startTime, effectiveTimezone)
      endIso = zonedWallClockToUtcIso(endDate || startDate, endTime, effectiveTimezone)
      const sMs = new Date(startIso).getTime()
      const eMs = new Date(endIso).getTime()
      if (eMs < sMs) {
        setErrorMsg(t('NLSEduEventValidationErrorEndBeforeStart'))
        return
      }
      if (eMs - sMs > 31 * 24 * 60 * 60 * 1000) {
        setErrorMsg(t('NLSEduEventValidationErrorTooLong'))
        return
      }
    }

    // RSVP Deadline
    let rsvpIso: string | null = null
    if (rsvpEnabled && rsvpDeadlineDate) {
      rsvpIso = zonedWallClockToUtcIso(rsvpDeadlineDate, rsvpDeadlineTime || '23:59', effectiveTimezone)
      const rsvpMs = new Date(rsvpIso).getTime()
      const startMs = allDay ? new Date(`${startIso}T00:00:00Z`).getTime() : new Date(startIso).getTime()
      if (rsvpMs > startMs) {
        setErrorMsg(t('NLSEduEventValidationErrorRsvpDeadline'))
        return
      }
    }

    try {
      setIsSubmitting(true)

      // Upload any new attachments first
      let uploadedFileIds: string[] = []
      if (selectedFiles.length > 0) {
        setUploadStatus(t('NLSEduEventUploadingAttachments'))
        uploadedFileIds = await uploadEduEventFiles({
          workspaceId,
          requestId: requestIdRef.current,
          files: selectedFiles,
        })
      }

      if (isEditing && event) {
        const keptFileIds = existingAttachments.map((a) => a.fileId)
        const allFileIds = [...keptFileIds, ...uploadedFileIds]

        await updateEduEvent({
          workspaceId,
          eventId: event.id,
          title: cleanTitle,
          description: description.trim() || undefined,
          location: location.trim() || undefined,
          onlineUrl: cleanUrl || undefined,
          allDay,
          startAt: startIso,
          endAt: endIso,
          timezone: effectiveTimezone,
          rsvpEnabled,
          rsvpDeadline: rsvpIso,
          reminderOffsetsMin: reminders,
          fileIds: allFileIds,
        })
      } else {
        await createEduEvent({
          workspaceId,
          audience: {
            kind: audienceKind,
            rootId: audienceKind === 'ROOT' ? selectedRootId : null,
            nodeIds: audienceKind === 'NODES' ? selectedNodeIds : [],
          },
          title: cleanTitle,
          description: description.trim() || undefined,
          location: location.trim() || undefined,
          onlineUrl: cleanUrl || undefined,
          allDay,
          startAt: startIso,
          endAt: endIso,
          timezone: effectiveTimezone,
          rsvpEnabled,
          rsvpDeadline: rsvpIso,
          reminderOffsetsMin: reminders,
          fileIds: uploadedFileIds,
          requestId: requestIdRef.current,
        })
      }

      onSaved()
      onClose()
    } catch (err: unknown) {
      const errKey = mapEduEventError(err)
      setErrorMsg(t(errKey))
    } finally {
      setIsSubmitting(false)
      setUploadStatus(null)
    }
  }

  return (
    <AppDialog
      open={isOpen}
      onDismiss={onClose}
      title={isEditing ? t('NLSEduEventEditorEditTitle') : t('NLSEduEventEditorCreateTitle')}
      size="lg"
      actions={[
        {
          label: t('NLSEduEventCancelButton'),
          onClick: onClose,
          variant: 'secondary',
          disabled: isSubmitting,
        },
        {
          label: isEditing ? t('NLSEduEventSaveButton') : t('NLSEduEventCreateButton'),
          onClick: handleSubmit,
          variant: 'primary',
          disabled: isSubmitting,
        },
      ]}
    >
      <div className="eduEventEditor">
        {errorMsg && (
          <div className="eduDialog__banner eduDialog__banner--error" role="alert">
            {errorMsg}
          </div>
        )}

        {uploadStatus && (
          <div className="eduDialog__banner eduDialog__banner--info">
            {uploadStatus}
          </div>
        )}

        {/* Audience Picker (only when creating) */}
        {!isEditing && (
          <EduAudiencePicker
            audienceKind={audienceKind}
            onChangeAudienceKind={(kind) => setAudienceKind(kind as EduEventAudienceKind)}
            selectedRootId={selectedRootId}
            onChangeSelectedRootId={setSelectedRootId}
            selectedNodeIds={selectedNodeIds}
            onChangeSelectedNodeIds={setSelectedNodeIds}
            availableRoots={availableRoots}
            availableClassNodes={availableClassNodes}
            audienceOptions={audienceOptions}
            labels={{
              audienceLabel: t('NLSEduEventAudienceLabel'),
              rootLabel: t('NLSEduEventAudienceRoot'),
            }}
            disabled={isSubmitting}
            sectionClassName="eduEventEditor__section"
            labelClassName="eduEventEditor__label"
            subSectionClassName="eduEventEditor__subSection"
            checkboxesClassName="eduEventEditor__nodeCheckboxes"
          />
        )}

        {/* Title */}
        <div className="eduEventEditor__field">
          <span className="eduEventEditor__label">{t('NLSEduEventTitleLabel')}</span>
          <TextInput
            value={title}
            onChange={(val) => setTitle(val)}
            placeholder={t('NLSEduEventTitlePlaceholder')}
            disabled={isSubmitting}
          />
        </div>

        {/* Description */}
        <div className="eduEventEditor__field">
          <span className="eduEventEditor__label">{t('NLSEduEventDescriptionLabel')}</span>
          <Textarea
            value={description}
            onChange={(val) => setDescription(val)}
            placeholder={t('NLSEduEventDescriptionPlaceholder')}
            rows={3}
            disabled={isSubmitting}
          />
        </div>

        {/* Location & Online URL */}
        <div className="eduEventEditor__row">
          <div className="eduEventEditor__field">
            <span className="eduEventEditor__label">{t('NLSEduEventLocationLabel')}</span>
            <TextInput
              value={location}
              onChange={(val) => setLocation(val)}
              placeholder={t('NLSEduEventLocationPlaceholder')}
              disabled={isSubmitting}
            />
          </div>
          <div className="eduEventEditor__field">
            <span className="eduEventEditor__label">{t('NLSEduEventOnlineUrlLabel')}</span>
            <TextInput
              value={onlineUrl}
              onChange={(val) => setOnlineUrl(val)}
              placeholder={t('NLSEduEventOnlineUrlPlaceholder')}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* All-Day Toggle */}
        <div className="eduEventEditor__toggleRow">
          <span className="eduEventEditor__label">{t('NLSEduEventAllDayLabel')}</span>
          <Toggle
            checked={allDay}
            onChange={(checked) => setAllDay(checked)}
            disabled={isSubmitting}
          />
        </div>

        {/* Date and Time Pickers */}
        <div className="eduEventEditor__dateTimeGrid">
          <div className="eduEventEditor__field">
            <span className="eduEventEditor__label">{t('NLSEduEventStartDateLabel')}</span>
            <DatePicker
              value={startDate}
              onChange={(date) => setStartDate(date || '')}
              disabled={isSubmitting}
              clearable={false}
              ariaLabel={t('NLSEduEventStartDateLabel')}
            />
          </div>

          {!allDay && (
            <div className="eduEventEditor__field">
              <span className="eduEventEditor__label">{t('NLSEduEventStartTimeLabel')}</span>
              <TimePicker
                value={startTime}
                onChange={(time) => setStartTime(time)}
                disabled={isSubmitting}
                clearable={false}
                ariaLabel={t('NLSEduEventStartTimeLabel')}
              />
            </div>
          )}

          <div className="eduEventEditor__field">
            <span className="eduEventEditor__label">{t('NLSEduEventEndDateLabel')}</span>
            <DatePicker
              value={endDate}
              onChange={(date) => setEndDate(date || '')}
              disabled={isSubmitting}
              clearable={false}
              ariaLabel={t('NLSEduEventEndDateLabel')}
            />
          </div>

          {!allDay && (
            <div className="eduEventEditor__field">
              <span className="eduEventEditor__label">{t('NLSEduEventEndTimeLabel')}</span>
              <TimePicker
                value={endTime}
                onChange={(time) => setEndTime(time)}
                disabled={isSubmitting}
                clearable={false}
                ariaLabel={t('NLSEduEventEndTimeLabel')}
              />
            </div>
          )}
        </div>

        {/* RSVP Section */}
        <div className="eduEventEditor__rsvpSection">
          <div className="eduEventEditor__toggleRow">
            <span className="eduEventEditor__label">{t('NLSEduEventRsvpSwitchLabel')}</span>
            <Toggle
              checked={rsvpEnabled}
              onChange={(checked) => setRsvpEnabled(checked)}
              disabled={isSubmitting}
            />
          </div>

          {rsvpEnabled && (
            <div className="eduEventEditor__dateTimeGrid">
              <div className="eduEventEditor__field">
                <span className="eduEventEditor__label">{t('NLSEduEventRsvpDeadlineLabel')}</span>
                <DatePicker
                  value={rsvpDeadlineDate}
                  onChange={(date) => setRsvpDeadlineDate(date || '')}
                  disabled={isSubmitting}
                  clearable={false}
                  ariaLabel={t('NLSEduEventRsvpDeadlineLabel')}
                />
              </div>
              <div className="eduEventEditor__field">
                <span className="eduEventEditor__label">{t('NLSEduEventEndTimeLabel')}</span>
                <TimePicker
                  value={rsvpDeadlineTime}
                  onChange={(time) => setRsvpDeadlineTime(time)}
                  disabled={isSubmitting}
                  clearable={false}
                  ariaLabel={t('NLSEduEventEndTimeLabel')}
                />
              </div>
            </div>
          )}
        </div>

        {/* Reminders */}
        <div className="eduEventEditor__section">
          <span className="eduEventEditor__label">{t('NLSEduEventRemindersLabel')}</span>
          <div className="eduEventEditor__checkboxGroup">
            <Checkbox
              label={t('NLSEduEventReminder1Week')}
              checked={reminders.includes(10080)}
              onChange={() => toggleReminder(10080)}
              disabled={isSubmitting}
            />
            <Checkbox
              label={t('NLSEduEventReminder1Day')}
              checked={reminders.includes(1440)}
              onChange={() => toggleReminder(1440)}
              disabled={isSubmitting}
            />
            <Checkbox
              label={t('NLSEduEventReminder1Hour')}
              checked={reminders.includes(60)}
              onChange={() => toggleReminder(60)}
              disabled={isSubmitting}
            />
            <Checkbox
              label={t('NLSEduEventReminder15Min')}
              checked={reminders.includes(15)}
              onChange={() => toggleReminder(15)}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Attachments Picker */}
        <div className="eduEventEditor__section">
          <span className="eduEventEditor__label">{t('NLSEduEventAttachmentsLabel')}</span>
          {existingAttachments.length > 0 && (
            <div className="eduEventEditor__fileList">
              {existingAttachments.map((att) => (
                <FileRow
                  key={att.fileId}
                  name={att.name}
                  sizeBytes={att.sizeBytes}
                  onRemove={
                    isSubmitting
                      ? undefined
                      : () =>
                          setExistingAttachments((prev) =>
                            prev.filter((a) => a.fileId !== att.fileId),
                          )
                  }
                />
              ))}
            </div>
          )}
          <FilePicker
            accept="image/*,video/*,application/pdf"
            multiple={true}
            maxBytes={25 * 1024 * 1024}
            onFiles={(files) => setSelectedFiles(files.slice(0, 5))}
            disabled={isSubmitting}
          />
          {selectedFiles.length > 0 && (
            <div className="eduEventEditor__fileList">
              {selectedFiles.map((file, idx) => (
                <FileRow
                  key={`${file.name}-${idx}`}
                  name={file.name}
                  sizeBytes={file.size}
                  onRemove={isSubmitting ? undefined : () => setSelectedFiles((prev) => prev.filter((_, i) => i !== idx))}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppDialog>
  )
}
